import { useRef, type Dispatch, type SetStateAction } from 'react'
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react'
import type { ProductGalleryImage } from '@/types'
import { MAX_GALLERY_IMAGES, moveGalleryImage } from '@/lib/productGallery'
import { menuPhotoFileToDataUrl } from './ProductPhotoField'

/**
 * "More photos": up to MAX_GALLERY_IMAGES extra pictures beside the main one,
 * shown in the photo strip when a customer opens the dish on the storefront.
 * Files go through the same downscale / data: URL path as the main
 * photo (menuPhotoFileToDataUrl). A new upload is a new entry, so it carries
 * no cutout; moved entries keep theirs.
 */
export function ProductGalleryField({
  value,
  onChange,
}: {
  value: ProductGalleryImage[]
  /** A state setter: updates are functional so a slow decode never works on a stale list. */
  onChange: Dispatch<SetStateAction<ProductGalleryImage[]>>
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const canAdd = value.length < MAX_GALLERY_IMAGES

  const iconBtn =
    'inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30'

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">More photos</p>
      <div className="flex flex-wrap items-start gap-3">
        {value.map((g, i) => (
          <div key={`${i}-${g.url.slice(0, 64)}`} className="space-y-1">
            <div className="relative">
              <img src={g.url} alt="" className="h-16 w-16 rounded-lg border object-cover" />
              <button
                type="button"
                className="absolute -right-2 -top-2 inline-flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm hover:text-foreground"
                aria-label={`Remove photo ${i + 1}`}
                onClick={() => onChange((prev) => prev.filter((_, j) => j !== i))}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </div>
            {value.length > 1 && (
              <div className="flex w-16 justify-between">
                <button
                  type="button"
                  className={iconBtn}
                  aria-label={`Move photo ${i + 1} left`}
                  disabled={i === 0}
                  onClick={() => onChange((prev) => moveGalleryImage(prev, i, -1))}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  className={iconBtn}
                  aria-label={`Move photo ${i + 1} right`}
                  disabled={i === value.length - 1}
                  onClick={() => onChange((prev) => moveGalleryImage(prev, i, 1))}
                >
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </div>
            )}
          </div>
        ))}
        {canAdd && (
          <button
            type="button"
            className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed bg-muted/40 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add photo
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Customers can swipe through these when they open the dish on your website.
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        className="sr-only"
        aria-label="Upload another product photo"
        onChange={(e) => {
          const f = e.target.files?.[0]
          const input = e.target
          if (!f) return
          void menuPhotoFileToDataUrl(f).then((url) => {
            input.value = ''
            if (!url) return
            // Cap re-checked against the latest list: a slow decode must
            // never push the gallery past the limit.
            onChange((prev) => (prev.length >= MAX_GALLERY_IMAGES ? prev : [...prev, { url }]))
          })
        }}
      />
    </div>
  )
}
